import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Checkbox, Stack } from "@wato/ui";
import type { LegalDocumentRef, LegalDocumentView } from "../../api/types";

/**
 * Explicit per-document acceptance (WP-418, FR-LEGAL-001): one checkbox per required document,
 * never implied by continuing. The server re-validates (`POST /v1/workspaces` rejects a missing
 * or stale version with 422), so this is UX — {@link allAccepted} only gates the button.
 */
const DOC_LABELS: Readonly<Record<string, MessageKey>> = {
  "terms-of-service": "dashboard.legal.docs.terms-of-service",
  "privacy-policy": "dashboard.legal.docs.privacy-policy",
  "acceptable-use-policy": "dashboard.legal.docs.acceptable-use-policy",
};

export const refKey = (d: LegalDocumentRef) => `${d.documentKey}@${d.version}`;

export function allAccepted(
  documents: readonly LegalDocumentView[],
  checked: ReadonlySet<string>,
): boolean {
  return documents.every((d) => checked.has(refKey(d)));
}

export interface LegalAcceptanceProps {
  readonly documents: readonly LegalDocumentView[];
  readonly checked: ReadonlySet<string>;
  readonly onToggle: (doc: LegalDocumentView) => void;
}

export function LegalAcceptance({ documents, checked, onToggle }: LegalAcceptanceProps) {
  const { t } = useI18n();
  return (
    <Stack gap={3} data-testid="legal-acceptance">
      <p className="text-sm text-fg-muted">{t("dashboard.legal.intro")}</p>
      {documents.map((doc) => {
        const labelKey = DOC_LABELS[doc.documentKey];
        const name = labelKey ? t(labelKey) : doc.documentKey;
        return (
          <Checkbox
            key={refKey(doc)}
            label={t("dashboard.legal.accept", { document: name })}
            hint={t("dashboard.legal.version", { version: doc.version })}
            checked={checked.has(refKey(doc))}
            onChange={() => onToggle(doc)}
            data-testid={`legal-checkbox-${doc.documentKey}`}
          />
        );
      })}
    </Stack>
  );
}
